// Client for the case-study API (apps/portal/portfolio-store.js). Sends the portal JWT when signed in.
(function () {
  const token = () => { try { return localStorage.getItem('wb_token') || ''; } catch (e) { return ''; } };

  async function request(method, url, body) {
    const headers = {};
    if (token()) headers.Authorization = `Bearer ${token()}`;
    const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
    if (body && !isForm) headers['Content-Type'] = 'application/json';
    const res = await fetch(url, { method, headers, body: body ? (isForm ? body : JSON.stringify(body)) : undefined });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
    return data;
  }

  // CRUD client for a media collection (videos, photos, photo-sets)
  const media = path => ({
    list: section => request('GET', `/api/portfolio/${path}${section ? `?section=${encodeURIComponent(section)}` : ''}`),
    create: item => request('POST', `/api/portfolio/${path}`, item),
    save: (id, item) => request('PUT', `/api/portfolio/${path}/${id}`, item),
    remove: id => request('DELETE', `/api/portfolio/${path}/${id}`)
  });

  // Chunked upload (each request stays under the proxy's body cap): start, PUT the chunks in order, finish -> { url }
  const chunked = async (route, file, onProgress) => {
    const { id, chunk } = await request('POST', `/api/portfolio/${route}`, { size: file.size, name: file.name });
    for (let offset = 0; offset < file.size; offset += chunk) {
      const res = await fetch(`/api/portfolio/${route}/${id}?offset=${offset}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/octet-stream', ...(token() ? { Authorization: `Bearer ${token()}` } : {}) },
        body: file.slice(offset, offset + chunk)
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `Upload failed (${res.status})`);
      if (onProgress) onProgress(Math.min(1, (offset + chunk) / file.size));
    }
    return request('POST', `/api/portfolio/${route}/${id}/finish`);
  };

  window.PortfolioApi = {
    // Admin › Site settings (apps/portal/site-settings.js)
    siteSettings: {
      get: () => request('GET', '/api/site-settings'),
      save: fonts => request('PUT', '/api/site-settings', { fonts })
    },
    // An installable font file (admin only): fetched with the session and handed to the browser as a download
    downloadFont: async file => {
      const res = await fetch(`/api/admin/fonts/${encodeURIComponent(file)}`, { headers: token() ? { Authorization: `Bearer ${token()}` } : {} });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `Download failed (${res.status})`);
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url; a.download = file; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    },
    hero: {
      get: (page = 'about') => request('GET', `/api/portfolio/hero?page=${encodeURIComponent(page)}`),
      save: (hero, page = 'about') => request('PUT', `/api/portfolio/hero?page=${encodeURIComponent(page)}`, hero)
    },
    pages: {
      list: () => request('GET', '/api/portfolio/pages'),
      create: body => request('POST', '/api/portfolio/pages', body),
      save: (key, body) => request('PUT', `/api/portfolio/pages/${encodeURIComponent(key)}`, body),
      remove: key => request('DELETE', `/api/portfolio/pages/${encodeURIComponent(key)}`),
      order: keys => request('PUT', '/api/portfolio/page-order', { keys })
    },
    // Deleted pages and sections wait here for 90 days (apps/portal/portfolio-trash.js)
    trash: {
      list: () => request('GET', '/api/portfolio/trash'),
      restore: (kind, key) => request('POST', `/api/portfolio/trash/${kind}/${encodeURIComponent(key)}/restore`),
      purge: (kind, key) => request('DELETE', `/api/portfolio/trash/${kind}/${encodeURIComponent(key)}`)
    },
    videos: media('videos'),
    photos: media('photos'),
    photoSets: media('photo-sets'),
    sections: {
      list: (page = 'about') => request('GET', `/api/portfolio/sections?page=${encodeURIComponent(page)}`),
      create: (title, template, page = 'about') => request('POST', '/api/portfolio/sections', { title, template, page }),
      save: (key, body) => request('PUT', `/api/portfolio/sections/${encodeURIComponent(key)}`, body),
      setImage: (key, imageUrl) => request('PUT', `/api/portfolio/sections/${encodeURIComponent(key)}/image`, { imageUrl }),
      remove: key => request('DELETE', `/api/portfolio/sections/${encodeURIComponent(key)}`),
      order: keys => request('PUT', '/api/portfolio/section-order', { keys })
    },
    list: section => request('GET', `/api/portfolio/studies${section ? `?section=${encodeURIComponent(section)}` : ''}`),
    create: (title, section) => request('POST', '/api/portfolio/studies', { title, section }),
    save: (slug, study) => request('PUT', `/api/portfolio/studies/${encodeURIComponent(slug)}`, study),
    remove: slug => request('DELETE', `/api/portfolio/studies/${encodeURIComponent(slug)}`),
    // Version history: the versions earlier saves replaced (apps/portal/portfolio-revisions.js)
    revisions: slug => request('GET', `/api/portfolio/studies/${encodeURIComponent(slug)}/revisions`),
    revision: (slug, id) => request('GET', `/api/portfolio/studies/${encodeURIComponent(slug)}/revisions/${id}`),
    order: slugs => request('PUT', '/api/portfolio/study-order', { slugs }),
    upload: file => { const f = new FormData(); f.append('image', file); return request('POST', '/api/portfolio/upload', f); },
    // onProgress(0..1) after every chunk
    uploadVideo: (file, onProgress) => chunked('video-upload', file, onProgress),
    // Downloadable files of any type, same chunked transfer; the original name is kept in the stored file name
    uploadFile: (file, onProgress) => chunked('file-upload', file, onProgress),
    uploadAudio: (file, onProgress) => chunked('audio-upload', file, onProgress)
  };
})();
